import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Box, Flex, Text } from 'atoms';
import { EyeIconSVG } from 'utils/Svg';
import FilePreviewModal from 'componentsv2/common/FilePreviewModal';
import { ACL } from 'components/common';

const RequestCard = styled(Box)`
	background: #fff;
	border: 1px solid #e8e8e8;
	border-radius: 8px;
	padding: 24px;
	margin-bottom: 16px;
	transition: box-shadow 0.3s;

	&:hover {
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}
`;

const CardBody = styled(Flex)`
	align-items: flex-start;
	justify-content: space-between;
	gap: 24px;
`;

const InfoColumns = styled(Box)`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 20px 40px;
	flex: 1;
`;

const InfoField = styled(Box)`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const FieldLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	font-weight: 400;
	line-height: 1.4;
	width: 140px;
`;

const FieldValue = styled(Text)`
	font-size: 15px;
	color: #000;
	font-weight: 600;
	line-height: 1.5;
`;

const ActionSection = styled(Flex)`
	flex-direction: row;
	align-items: center;
	justify-content: center;
	gap: 12px;
	flex-shrink: 0;
	align-self: center;
`;

const ViewButton = styled(Box)`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 4px;
	border: none;
	cursor: pointer;
	color: #000;
	font-size: 16px;
	transition: all 0.2s ease;
	margin-right: 15px;
	padding: 0;

	span {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px !important;
		height: 20px !important;
		margin-right: 0 !important;
	}

	svg {
		width: 20px !important;
		height: 20px !important;
		max-width: 20px;
		max-height: 20px;
	}

	&:hover {
		background: #bae7ff;
	}

	&:active {
		background: #91d5ff;
	}
`;

const ApproveButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;

	padding: 8px 18px;
	min-width: 130px;

	background: #fff;
	color: #52c41a;

	border: 1.5px solid #52c41a;
	border-radius: 6px;

	font-size: 14px;
	font-weight: 500;
	cursor: pointer;

	transition: all 0.25s ease;

	.anticon {
		font-size: 16px;
		color: #52c41a;
	}

	&:hover {
		background: #f6ffed;
		border-color: #73d13d;
		color: #73d13d;
	}

	&:active {
		background: #d9f7be;
		border-color: #52c41a;
	}
`;

const RejectButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	padding: 10px 20px;
	background: #fff;
	color: #f44336;
	border: 1.5px solid #f44336;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.3s;
	min-width: 130px;

	.anticon {
		font-size: 16px;
	}

	&:hover {
		background: #ffebee;
		border-color: #e57373;
		color: #e57373;
		transform: translateY(-1px);
	}

	&:active {
		background: #fff;
		border-color: #d32f2f;
		color: #d32f2f;
		transform: translateY(0);
	}
`;

const DocumentLink = styled.a`
	color: #1890ff;
	text-decoration: none;
	font-size: 15px;
	font-weight: 600;

	&:hover {
		text-decoration: underline;
		color: #40a9ff;
	}
`;

const maskAccountNumber = (accountNumber) => {
	if (!accountNumber) return '-';
	if (accountNumber.length <= 8) return accountNumber;
	return `${accountNumber.substring(0, 3)}${'*'.repeat(
		accountNumber.length - 6
	)}${accountNumber.substring(accountNumber.length - 3)}`;
};

export default function VendorBankDetailsCardV2({ bankDetails, onView, onApprove, onReject }) {
	const [previewVisible, setPreviewVisible] = useState(false);

	return (
		<>
			<RequestCard>
				<CardBody>
					<InfoColumns>
						<InfoField>
							<FieldLabel>Vendor Name :</FieldLabel>
							<FieldValue>
								<strong>{bankDetails.vendor_name || '-'}</strong>
							</FieldValue>
						</InfoField>

						<InfoField>
							<FieldLabel>Bank Account Number :</FieldLabel>
							<FieldValue>
								<strong>{maskAccountNumber(bankDetails.account_number)}</strong>
							</FieldValue>
						</InfoField>

						<InfoField>
							<FieldLabel>IFSC Code :</FieldLabel>
							<FieldValue>
								<strong>{bankDetails.ifsc_code || '-'}</strong>
							</FieldValue>
						</InfoField>

						<InfoField>
							<FieldLabel>Bank Name :</FieldLabel>
							<FieldValue>
								<strong>{bankDetails.bank_name || '-'}</strong>
							</FieldValue>
						</InfoField>

						<InfoField>
							<FieldLabel>Bank Branch :</FieldLabel>
							<FieldValue>{bankDetails.branch_name || '-'}</FieldValue>
						</InfoField>

						<InfoField>
							<FieldLabel>Document Attached :</FieldLabel>
							<FieldValue>
								{bankDetails.document_key ? (
									<DocumentLink
										onClick={() => setPreviewVisible(true)}
										style={{ cursor: 'pointer' }}
									>
										{bankDetails.document_name || 'View Document'}
									</DocumentLink>
								) : (
									'-'
								)}
							</FieldValue>
						</InfoField>
					</InfoColumns>
					<ActionSection>
						<ViewButton onClick={() => onView(bankDetails.id)}>
							<EyeIconSVG />
						</ViewButton>
						<ACL action="update" module="handle_vendor_bank_details_approval">
							<ApproveButton onClick={() => onApprove(bankDetails.id)}>
								<CheckOutlined />
								Approve
							</ApproveButton>
						</ACL>
						<ACL action="update" module="handle_vendor_bank_details_approval">
							<RejectButton onClick={() => onReject(bankDetails.id)}>
								<CloseOutlined />
								Reject
							</RejectButton>
						</ACL>
					</ActionSection>
				</CardBody>
			</RequestCard>

			{/* Document Preview Modal */}
			{bankDetails.document_key && (
				<FilePreviewModal
					visible={previewVisible}
					fileKey={bankDetails.document_key}
					fileName={bankDetails.document_name || 'Bank Document'}
					entity="vendor"
					onClose={() => setPreviewVisible(false)}
				/>
			)}
		</>
	);
}

VendorBankDetailsCardV2.propTypes = {
	bankDetails: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		vendor_name: PropTypes.string,
		account_number: PropTypes.string,
		ifsc_code: PropTypes.string,
		bank_name: PropTypes.string,
		branch_name: PropTypes.string,
		document_key: PropTypes.string,
		document_name: PropTypes.string,
	}).isRequired,
	onView: PropTypes.func.isRequired,
	onApprove: PropTypes.func.isRequired,
	onReject: PropTypes.func.isRequired,
};
